import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Grid,
  Typography,
  Button,
} from "@mui/material";
import Alert from "@mui/material/Alert"; // Import Alert for Snackbar
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";

const Edit = ({ open, onClose, statusDetail, onRecordUpdated }) => {
  const { updateData, deleteData } = useFirestore("CourseShellStatus"); // Specify collection
  const [formData, setFormData] = useState({
    id: "", // To store Firestore document ID
    Name: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  useEffect(() => {
    if (statusDetail) {
      console.log(
        "CourseShellStatus Edit - statusDetail received:",
        statusDetail
      );

      setFormData({
        id: statusDetail.id || "",
        Name: statusDetail.Name || "",
      });

      console.log("CourseShellStatus Edit - formData set to:", {
        Name: statusDetail.Name || "",
      });
    } else {
      // Reset form if statusDetail is null
      setFormData({
        id: "",
        Name: "",
      });
    }
  }, [statusDetail]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.Name.trim()) {
      setSnackbarMessage("Name is required!");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    try {
      if (!formData.id) {
        throw new Error("Document ID is missing for update.");
      }
      await updateData(formData.id, formData); // Pass ID and data separately
      setLoading(false);
      setSnackbarMessage("Record has been updated successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close dialog on success
    } catch (error) {
      console.error("Error updating record:", error);
      setLoading(false);
      setSnackbarMessage(`Error updating record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (!statusDetail || !statusDetail.id) {
        throw new Error("Document ID is missing for delete.");
      }
      await deleteData(statusDetail.id);
      setLoading(false);
      setSnackbarMessage("Record has been deleted successfully!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      if (onRecordUpdated) onRecordUpdated();
      onClose(); // Close the main dialog
    } catch (error) {
      console.error("Error deleting record:", error);
      setLoading(false);
      setSnackbarMessage(`Error deleting record: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleConfirmDeleteOpen = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDeleteClose = () => {
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = () => {
    handleConfirmDeleteClose();
    handleDelete();
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // The X (and backdrop/Escape, which share the same Dialog onClose) always
  // asks for confirmation first rather than closing immediately, so an
  // in-progress edit isn't lost by an accidental click.
  const handleCloseAttempt = () => {
    setConfirmCloseOpen(true);
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
        <ModalDialogTitle onClose={handleCloseAttempt}>
          Edit Course Shell Status
        </ModalDialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                name="Name"
                value={formData.Name}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                autoFocus
              />
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions
          onSave={handleSubmit}
          saving={loading}
          onDelete={handleConfirmDeleteOpen}
        />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={handleConfirmDeleteClose}
        onConfirm={handleConfirmDelete}
        deleting={loading}
        message="Are you sure you want to delete this course shell status?"
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Edit;
